import type { DegradationPolicy } from './api';

const STEPS = [1, 0.85, 0.7, 0.6, 0.5, 0.4, 0.33, 0.25];
/** Seconds of sustained overload before stepping down, and of headroom before stepping up. */
const DOWN_AFTER = 2;
const UP_AFTER = 5;
const OVERLOAD_RATIO = 0.85;
const HEADROOM_RATIO = 0.55;

export interface LoadSample {
  frameTimeAvgMs: number;
  intervalMs: number;
  /** Frames composed in the window. */
  renderedFrames: number;
  /**
   * Slots lost because composing overran. Slots lost because the thread did
   * not run (starvation) must not be included: a lower render scale cannot
   * help with those.
   */
  skippedFrames: number;
}

/**
 * Decides the internal render scale from one load sample per second. Stepping
 * up requires the projected cost at the higher scale to leave headroom, so the
 * scale does not oscillate around the overload threshold.
 */
export class DegradationController {
  scale = 1;
  private policy: DegradationPolicy = { auto: false, minRenderScale: 0.5 };
  private overloaded = 0;
  private healthy = 0;

  setPolicy(policy: DegradationPolicy): void {
    this.policy = { auto: policy.auto, minRenderScale: Math.min(1, Math.max(0.1, policy.minRenderScale)) };
    this.overloaded = 0;
    this.healthy = 0;
    if (!policy.auto) this.scale = 1;
    else if (this.scale < this.policy.minRenderScale) this.scale = this.stepAtOrAbove(this.policy.minRenderScale);
  }

  sample(load: LoadSample): number {
    if (!this.policy.auto) {
      this.scale = 1;
      return this.scale;
    }
    const total = load.renderedFrames + load.skippedFrames;
    const dropping = total > 0 && load.skippedFrames > total * 0.1;
    if (dropping || load.frameTimeAvgMs > load.intervalMs * OVERLOAD_RATIO) {
      this.healthy = 0;
      if (++this.overloaded >= DOWN_AFTER) {
        this.overloaded = 0;
        this.scale = this.lower();
      }
      return this.scale;
    }
    this.overloaded = 0;
    const up = this.higher();
    // Fill cost grows with pixel count, i.e. with the square of the scale.
    const projected = load.frameTimeAvgMs * (up / this.scale) ** 2;
    if (up > this.scale && load.skippedFrames === 0 && projected < load.intervalMs * HEADROOM_RATIO) {
      if (++this.healthy >= UP_AFTER) {
        this.healthy = 0;
        this.scale = up;
      }
    } else {
      this.healthy = 0;
    }
    return this.scale;
  }

  private stepAtOrAbove(min: number): number {
    let best = 1;
    for (const step of STEPS) if (step >= min) best = step;
    return best;
  }

  private lower(): number {
    for (const step of STEPS) {
      if (step < this.scale - 1e-6) return step >= this.policy.minRenderScale ? step : this.scale;
    }
    return this.scale;
  }

  private higher(): number {
    let result = this.scale;
    for (const step of STEPS) if (step > this.scale + 1e-6) result = step;
    return result;
  }
}
